<template>
  <div class="ndc-submission-detail">
    <div class="drawer-header">
      {{ entry.quarter_name }}
      <div class="header-action" style="display:flex;align-items:center;gap:12px;">
        <button class="btn header_sbtn" @click="$emit('close')">CANCEL</button>
        <button
          v-if="hasPendingNewUpload && !isSubmitted"
          class="btn header_pbtn"
          :disabled="!hasPendingNewUpload || uploading || isSubmitted"
          @click="save">
          {{ $t('MDM_SAVE') }}
        </button>
      </div>
    </div>

    <div class="ndc-body">
      <div class="ndc-template-section">
        <p class="ndc-section-title">{{ $t('MDM_DOWNLOAD_TEMPLATES') }}</p>
        <div class="ndc-template-row">
          <div class="ndc-template-info">
            <img :src="DOC" width="30" height="30" alt="doc" />
            <span class="ndc-template-name">{{ $t('MDM_CLEAR_NDC_TEMPLATE') }}</span>
          </div>
          <button class="ndc-btn-download" @click="downloadTemplate('CLEAR_NDC')">{{ $t('MDM_DOWNLOAD') }}</button>
        </div>
        <div class="ndc-template-row">
          <div class="ndc-template-info">
            <img :src="EXCEL" width="30" height="30" alt="xls" />
            <span class="ndc-template-name">{{ $t('MDM_NDC_WITH_OUTSTANDING_INDENT_TEMPLATE') }}</span>
          </div>
          <button class="ndc-btn-download" @click="downloadTemplate('NDC_WITH_OUTSTANDING_INDENT')">{{ $t('MDM_DOWNLOAD') }}</button>
        </div>
      </div>

      <div class="ndc-upload-section">
        <div class="ndc-upload-row">
          <div class="ndc-upload-label">
            <p class="upload-title">{{ $t('MDM_CLEAR_NDC') }}</p>
            <p class="upload-hint">{{ $t('MDM_CNDC_DOCUMENT_TYPES') }}</p>
          </div>
          <div class="ndc-upload-attach">
            <Attachments
              :attachments="cndcAttachments"
              :allowedFileFormats="cndcAllowedFormats"
              :maxAllowed="1"
              :showPreview="true"
              :allowDelete="!isSubmitted"
              :disableUpload="isCndcUploaded || isOndcUploaded || isSubmitted"
              @addAttachments="(file) => onFileAdded(file, 'cndc')"
              @removeAttachments="onRemove" />
          </div>
        </div>

        <div class="ndc-upload-row mt-20">
          <div class="ndc-upload-label">
            <p class="upload-title">{{ $t('MDM_NDC_WITH_OUTSTANDING_INDENT') }}</p>
            <p class="upload-hint">{{ $t('MDM_ONDC_DOCUMENT_TYPES') }}</p>
          </div>
          <div class="ndc-upload-attach">
            <Attachments
              :attachments="ondcAttachments"
              :allowedFileFormats="ondcAllowedFormats"
              :maxAllowed="1"
              :showPreview="true"
              :allowDelete="!isSubmitted"
              :disableUpload="isOndcUploaded || isCndcUploaded || isSubmitted"
              @addAttachments="(file) => onFileAdded(file, 'ondc')"
              @removeAttachments="onRemove" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Attachments from '@common/components/Attachments';
import UploadMixin from '@common/mixins/UploadMixin';
import downloadFile from '@common/utils/downloadFile';
import { ndcSubmissionStatus } from 'pando-constants';
import { uploadNdcDocument, deleteNdcAttachment, showNdcEntry, downloadCndcTemplate, downloadOndcTemplate } from '@/api-factory/ndc';

const CLEAR_NDC = 'CLEAR_NDC';
const ONDC = 'NDC_WITH_OUTSTANDING_INDENT';

export default {
  components: { Attachments },
  mixins: [UploadMixin],
  props: {
    entry: { type: Object, required: true },
  },
  data() {
    return {
      cndcAttachments: [],
      ondcAttachments: [],
      cndcAllowedFormats: ['pdf', 'doc', 'docx'],
      ondcAllowedFormats: ['csv', 'xlsx', 'xls'],
      pendingCndcFile: null,
      pendingOndcFile: null,
      uploading: false,
      ndcSubmissionStatus,
    };
  },
  computed: {
    isSubmitted() {
      return this.entry.submission_status === ndcSubmissionStatus.SUBMITTED;
    },
    isCndcUploaded() {
      return this.cndcAttachments.length > 0;
    },
    isOndcUploaded() {
      return this.ondcAttachments.length > 0;
    },
    hasPendingNewUpload() {
      return !!(this.pendingCndcFile || this.pendingOndcFile);
    },
    attachments() {
      return [...this.cndcAttachments, ...this.ondcAttachments];
    },
  },
  watch: {
    entry: {
      immediate: true,
      handler(val) {
        this.initFromEntry(val);
      },
    },
  },
  methods: {
    initFromEntry(entry) {
      this.pendingCndcFile = null;
      this.pendingOndcFile = null;
      this.cndcAttachments = (entry.cndc_attachments || []).map(a => ({
        name: a.file_name,
        url: a.url,
        id: a.id,
      }));
      this.ondcAttachments = (entry.ondc_attachments || []).map(a => ({
        name: a.file_name,
        url: a.url,
        id: a.id,
      }));
    },
    onFileAdded(file, type) {
      if (type === 'cndc') {
        this.pendingCndcFile = file;
        this.cndcAttachments = [{ name: file.name, url: file.url, raw: file.raw }];
      } else {
        this.pendingOndcFile = file;
        this.ondcAttachments = [{ name: file.name, url: file.url, raw: file.raw }];
      }
    },
    async onRemove(file) {
      if (!file.id) {
        this.pendingCndcFile = null;
        this.pendingOndcFile = null;
        this.cndcAttachments = [];
        this.ondcAttachments = [];
        return;
      }
      try {
        await deleteNdcAttachment(this.entry.id);
        const updatedEntry = await showNdcEntry(this.entry.id);
        this.$successMessage({ message: 'Document deleted successfully' });
        this.$emit('deleted', updatedEntry);
      } catch (error) {
        const message = (error && error.data && error.data.errors && error.data.errors[0]) || 'Delete failed';
        this.$errorMessage({ message });
      }
    },
    async downloadTemplate(type) {
      const isOndc = type === ONDC;
      try {
        const blob = isOndc
          ? await downloadOndcTemplate(this.entry.id)
          : await downloadCndcTemplate(this.entry.id);
        const quarterName = this.entry.quarter_name.replace(/\s+/g, '-');
        const fileName = isOndc
          ? `ndc-outstanding-${quarterName}.xlsx`
          : `clear-ndc-${quarterName}.doc`;
        const mimeType = isOndc
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/msword';
        const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
        downloadFile(document, url, fileName);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$errorMessage({ message: 'Failed to download template' });
      }
    },
    async save() {
      this.uploading = true;
      try {
        if (this.pendingCndcFile) await this.uploadFile(this.pendingCndcFile.raw, CLEAR_NDC);
        if (this.pendingOndcFile) await this.uploadFile(this.pendingOndcFile.raw, ONDC);
        const updatedEntry = await showNdcEntry(this.entry.id);
        this.$successMessage({ message: 'Document(s) uploaded successfully' });
        this.$emit('uploaded', updatedEntry);
      } catch (error) {
        const message = (error && error.data && error.data.errors && error.data.errors[0]) || 'Upload failed';
        this.$errorMessage({ message });
      } finally {
        this.uploading = false;
      }
    },
    async uploadFile(file, submissionType) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('submission_type', submissionType);
      return uploadNdcDocument(this.entry.id, formData);
    },
  },
};
</script>

<style scoped>
.ndc-body {
  padding: 20px;
  height: calc(100% - 50px);
  overflow-y: auto;
}
.ndc-template-section {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 14px 16px;
  margin-bottom: 24px;
}
.ndc-section-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a6fe8;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.ndc-template-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e8ecf0;
}
.ndc-template-row:last-child {
  border-bottom: none;
}
.ndc-template-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ndc-template-name {
  font-size: 1.3rem;
  color: #333;
}
.ndc-btn-download {
  background: none;
  border: none;
  color: #1a6fe8;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-transform: uppercase;
}
.ndc-upload-section {
  padding: 0 4px;
}
.ndc-upload-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.ndc-upload-label {
  flex: 1;
  padding-top: 4px;
}
.ndc-upload-attach {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.upload-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}
.upload-hint {
  font-size: 1.1rem;
  color: #888;
}
</style>

<style>
.ndc-upload-attach .uploadsetion {
  padding: 0;
}
.ndc-upload-attach .text-grey-medium1 {
  display: none;
}
.ndc-upload-attach .el-upload--picture-card,
.ndc-upload-attach .el-upload-list--picture-card {
  vertical-align: middle;
  margin: 0;
}
</style>
